import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, Card, CardContent, CardMedia, FormControl, Stack, TextField, Typography } from '@mui/material';
import { Api } from '../../services/api';
import { Recipe, RecipeSchema } from '../../services/api/models';
import { Form, useParams, useNavigate, Link } from 'react-router-dom';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CategorySelector from '../../components/CategorySelector';
import CuisineSelector from '../../components/CuisineSelector';
import IngredientsEditTable from '../../components/IngredientsEditTable';
import SaveIcon from '@mui/icons-material/Save';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DurationField } from '../../components/DurationField';
import StepsEditor from '../../components/StepsEditor';

// Component definition
function EditRecipePage() {
  const {recipeId} = useParams();
  const [notFound, setNotFound] = useState(false);

  const methods = useForm<Recipe>({
    resolver: zodResolver(RecipeSchema), // Apply the zodResolver
    defaultValues: {},
  });
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, defaultValues },
  } = methods;

  const { fields } = useFieldArray({
    control,
    name: 'tags',
  });

  const navigate = useNavigate();

  const onSubmit = async (recipe: Recipe) => {
  	console.log('SUCCESS', recipe);

    if (recipeId) {
      // update
      Api.Recipe.put(recipe)
        .then(response => {
          console.log(response.data.recipe);
          navigate(`/recipe/${response.data.recipe.id}`);
        }).catch(error => {
          console.warn(error);
          setNotFound(true);
        });

    } else {
      // create
      Api.Recipe.post(recipe)
        .then(response => {
          console.log(response.data.recipe);
          navigate(`/recipe/${response.data.recipe.id}`);
        }).catch(error => {
          console.warn(error);
          setNotFound(true);
        });
    }
  }


  useEffect(() => {
    if (!recipeId) {
      return;
    }

    // call api or anything
    async function fetchRecipe(recipeId: string) {
      Api.Recipe.get(recipeId)
        .then(response => {
          reset(response.data.recipe);
        }).catch(error => {
          console.warn(error);
          setNotFound(true);
        });
    }

    if (recipeId) {
      fetchRecipe(recipeId)
    }
 }, [recipeId, reset]);

  if (!notFound) {
    return (
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Alert>{JSON.stringify(errors)}</Alert>
          <Card>
            { getValues('images') &&
            <CardMedia
              sx={{ height: 250 }}
              image={getValues('images.0.url')}
              title="green iguana"
            />
            }
            <CardContent>
            <Box>
              <TextField
                variant='outlined'
                label='Recipe Name'
                defaultValue={defaultValues?.name}
                error={!!errors.name}
                helperText={errors.name?.message}
                InputLabelProps={{ shrink: true }}
                {...register('name')}
              />
            </Box>
            <Box>
              <TextField
                label="Description"
                defaultValue={defaultValues?.description}
                error={!!errors.description}
                InputLabelProps={{ shrink: true }}
                {...register('description')}
                multiline
              />
            </Box>
            <Box>
              <TextField
                variant='outlined'
                label='Author'
                defaultValue={defaultValues?.author}
                error={!!errors.author}
                helperText={errors.author?.message}
                InputLabelProps={{ shrink: true }}
                {...register('author')}
              />
            </Box>
            <Box>
              <CategorySelector label='Category' control={control}/>
            </Box>
            <Box>
              <CuisineSelector label='Cuisine' control={control}/>
            </Box>
            <Box>
              <FormControl>
                <TextField
                  label='Yields'
                  defaultValue={defaultValues?.yields}
                  error={!!errors.yields}
                  helperText={errors.yields?.message}
                  InputLabelProps={{ shrink: true }}
                  {...register('yields')}
                />
              </FormControl>
            </Box>
            <Stack direction="row" spacing={2}>
              <Card>
                <FormControl>
                  <DurationField name='prep_time' label='Prep Time' control={control}/>
                </FormControl>
              </Card>
              <Card>
                <FormControl>
                  <DurationField name='cook_time' label='Cook Time' control={control}/>
                </FormControl>
              </Card>
              <Card>
                <FormControl>
                  <DurationField name='total_time' label='Total Time' control={control}/>
                </FormControl>
              </Card>
            </Stack>
            <Typography variant="h2">Ingredients</Typography>
            <IngredientsEditTable showSort={true} />
            <StepsEditor />
            <Card>
              <Stack direction="row" spacing={2}>
                  {fields.map((tag, i) => (
                    <Card key={i}>
                      <Typography variant="h5"><LocalOfferOutlinedIcon sx={{fontSize: 15}}/> {tag.name}</Typography>
                    </Card>
                  ))}
              </Stack>
            </Card>
            </CardContent>
            <Box display='flex' alignItems='center' justifyContent='center'>
              <Button variant='outlined' component={Link} to={`/recipe/${recipeId}`}>Cancel</Button>
              <Button type='submit' variant='contained' startIcon={<SaveIcon />} sx={{margin: 2}}>Save</Button>
            </Box>
          </Card>
        </Form>
      </FormProvider>
    );
  } else {
    return (
      <Box>
        <Typography variant='h1'>Recipe Not Found</Typography>
      </Box>
    )
  }
}

// Default export
export default EditRecipePage;
